/** Placeholder in the ledger check-number column. Same idea as typing `>>`. */
const PRINT_CHECK_NUMBER = 'PRINT';

function isPrintCheckNumber(value) {
  return String(value ?? '').trim().toUpperCase() === PRINT_CHECK_NUMBER;
}

/** Next number after GET /api/get-max-num, matching Ledger Entry `>>`. */
function nextCheckNumber(maxNum) {
  const n = Number(maxNum);
  if (!Number.isFinite(n)) return null;
  return n + 1;
}

/**
 * Rows that still say PRINT. A split parent save rewrites every child Num,
 * so those children are not saved on their own.
 */
function printPlaceholderEntries(ledgerData) {
  const parent = ledgerData && ledgerData.parentEntry;
  if (!parent) return [];
  if (isPrintCheckNumber(parent.Num)) return [parent];
  const siblings = Array.isArray(ledgerData.siblings) ? ledgerData.siblings : [];
  return siblings.filter((row) => row && isPrintCheckNumber(row.Num));
}

function ledgerDateForSave(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr).trim();
  const mdY = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (mdY) {
    let year = mdY[3];
    if (year.length === 2) year = `20${year}`;
    return `${year}-${mdY[1].padStart(2, '0')}-${mdY[2].padStart(2, '0')} 00:00:00`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.includes(' ') ? s.slice(0, 19) : `${s.slice(0, 10)} 00:00:00`;
  }
  return null;
}

/** Full ledger update. A Num-only post would wipe the other columns. */
function ledgerCheckNumberUpdate(entry, num) {
  if (!entry || entry.Id == null) return null;
  const date = ledgerDateForSave(entry.Date);
  if (!date) return null;
  return {
    Id: entry.Id,
    Date: date,
    Type: entry.Type || '',
    Num: String(num),
    Amount: entry.Amount,
    Name: entry.Name || '',
    Memo: entry.Memo || '',
    Account: entry.Account || '',
    Split: entry.Split || '',
    Class: entry.Class || '',
    IsSplitParent: entry.IsSplitParent ? 1 : 0,
    LinkedSplitId: entry.LinkedSplitId ?? null,
  };
}

/**
 * Write `nextNum` onto the PRINT rows in memory.
 * A split parent also updates every child, matching the server cascade.
 * Returns the ledger ids whose check number changed.
 */
function applyAssignedCheckNumber(ledgerData, nextNum) {
  const parent = ledgerData && ledgerData.parentEntry;
  const targets = printPlaceholderEntries(ledgerData);
  if (!parent || !targets.length || nextNum == null) return [];
  const next = String(nextNum);
  const parentIsTarget = targets.some(
    (row) => row === parent || Number(row.Id) === Number(parent.Id),
  );
  const ids = [];
  if (parentIsTarget) {
    parent.Num = next;
    ids.push(parent.Id);
    if (parent.IsSplitParent && Array.isArray(ledgerData.siblings)) {
      for (const sibling of ledgerData.siblings) {
        if (!sibling) continue;
        sibling.Num = next;
        ids.push(sibling.Id);
      }
    }
    return ids;
  }
  for (const row of targets) {
    row.Num = next;
    ids.push(row.Id);
  }
  return ids;
}

module.exports = {
  PRINT_CHECK_NUMBER,
  isPrintCheckNumber,
  nextCheckNumber,
  printPlaceholderEntries,
  ledgerDateForSave,
  ledgerCheckNumberUpdate,
  applyAssignedCheckNumber,
};
